import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useRealLocation } from '@/app/realLocation';
import { useTaskDetail } from '@/hooks';
import NuevaDiligenciaModal from './NuevaDiligenciaModal';
import { useCreateDiligencia } from './useCreateDiligencia';

// Route-anchored host for the create-diligencia modal. The modal is independent
// of any page: it opens whenever the real location is /diligencias/nueva and
// mounts OVER the current view — AppRoutes renders the `background` location a
// trigger passed in navigation state, while this host (mounted in MainLayout)
// reads the real one — so opening it from the detail, a causa or the dashboard
// keeps that view mounted, and closing it restores the view untouched. A deep
// link carries no background, so AppRoutes falls back to the real location and
// the list renders behind the modal. App-wide pattern from here on:
// route-anchored modals are independent components mounted over the current
// view, never inside a page.
//
// The real location comes from RealLocationContext, NOT useLocation: this host
// lives inside <Routes location={background || location}>, and React Router
// scopes LocationContext there to the matched (background) location, which
// never has the modal's pathname.
//
// Split on purpose: the outer host guards on the pathname BEFORE any data hook
// runs, so the query/create hooks mount only while the modal route is active
// (MainLayout renders the host on every page).
export default function NuevaDiligenciaHost() {
  const location = useRealLocation();
  if (!location || location.pathname !== '/diligencias/nueva') return null;
  return <NuevaDiligenciaOpen nuevaState={location.state || {}} />;
}

function NuevaDiligenciaOpen({ nuevaState }) {
  const navigate = useNavigate();

  // Re-encargar: the lane row lacks address/comuna/notes, so the source task
  // detail supplies the form's initial values. The deadline stays empty: a
  // closed diligencia's plazo is already past and the form rejects past dates.
  const fromTaskId = nuevaState.fromTaskId ?? null;
  const { data: fromTask, isLoading: fromTaskLoading } = useTaskDetail(
    fromTaskId,
    { enabled: Boolean(fromTaskId) },
  );
  const nuevaInitial = fromTask
    ? {
        comuna: fromTask.commune ?? null,
        region: fromTask.region ?? null,
        address: fromTask.address ?? '',
        detalle: fromTask.addressDetails ?? '',
        notes: fromTask.notes ?? '',
        // Lo que se pidió (REC-1966): el nuevo encargo parte de lo mismo.
        actuationType: fromTask.actuationType ?? '',
        actuationTypeOther: fromTask.actuationTypeOther ?? '',
        conservador: fromTask.conservador ?? null,
        addressType: fromTask.addressType ?? '',
        recipients: fromTask.recipients ?? null,
      }
    : undefined;
  const lockedCase = nuevaState.caseId
    ? {
        caseId: nuevaState.caseId,
        rol: nuevaState.rol,
        caratula: nuevaState.caratula,
        courtName: nuevaState.courtName,
        tribunalName: nuevaState.tribunalName ?? fromTask?.tribunalName,
      }
    : null;

  const { create, busy: creating } = useCreateDiligencia();

  // With a background, the modal was pushed over a live view: going back pops
  // to it with its state intact. A deep link has nothing behind → the list.
  const close = () => {
    if (nuevaState.background) navigate(-1);
    else navigate('/diligencias');
  };

  return (
    <NuevaDiligenciaModal
      // The form snapshots `initial`/`lockedCase` into state at mount: keep
      // the modal closed until the source detail arrives. On a failed fetch
      // it opens with the causa alone.
      open={!(fromTaskId && fromTaskLoading)}
      onClose={close}
      mode="create"
      lockedCase={lockedCase}
      initial={nuevaInitial}
      onSubmit={create}
      isSubmitting={creating}
    />
  );
}

NuevaDiligenciaOpen.propTypes = {
  nuevaState: PropTypes.object.isRequired,
};
